import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import useGetDiscount from '@/features/dashboard/discounts/hooks/useGetDiscount.ts';
import useUpdateDiscount from '@/features/dashboard/discounts/hooks/useUpdateDiscount.ts';
import {
  discountSchema,
  DiscountFormData,
  DiscountInputData,
} from '@/features/dashboard/discounts/schemas/discountSchema.ts';
import { discountMapper } from '@/features/dashboard/discounts/types/discountMapper.ts';
import DiscountForm from '@/features/dashboard/discounts/components/DiscountForm.tsx';
import DiscountFormSkeleton from '@/features/dashboard/discounts/components/DiscountFormSkeleton.tsx';

interface EditDiscountModalProps {
  discountId: string;
  onClose: () => void;
}

const EditDiscountModal = ({ discountId, onClose }: EditDiscountModalProps) => {
  const { data: discount, isLoading } = useGetDiscount(discountId);
  const updateMutation = useUpdateDiscount(discountId);

  const methods = useForm<DiscountInputData>({
    resolver: zodResolver(discountSchema),
    values: discount ? discountMapper.fromDtoToForm(discount) : undefined,
  });

  const { handleSubmit } = methods;

  const onSubmit = (data: DiscountFormData) => {
    const dto = discountMapper.toUpdateDto(data);
    updateMutation.mutate(dto, { onSuccess: () => onClose() });
  };

  return (
    <Modal onClose={onClose} isDirty={methods.formState.isDirty}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader title="Edytuj kod promocyjny" onClose={onClose} />
          <ModalContent>
            {isLoading ? <DiscountFormSkeleton /> : <DiscountForm isEdit />}
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={updateMutation.isPending}
              disabled={isLoading}
            >
              Zapisz zmiany
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default EditDiscountModal;
